// QTetris in the browser: the game (pygbag) in a frame. The server hands out the bundle with exactly the levels of
// the user's plan - Level 1 on every plan, Levels 2 and 3 where the plan (or an admin) includes them; a level that is
// not included is not in the game the browser receives, and shows as a locked button there.
import { Alert, Badge, Button, Card, Group, Loader, Stack, Table, Text } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { ErrorBox, RequestAccessModal } from "../components/Errors";
import { PageTitle } from "../components/Layout";
import { RefLinks } from "../components/RefLinks";
import { ApiError, post } from "../lib/api";
import { tagged, useReferences } from "../lib/references";

export interface TetrisSession { url: string; levels: number[]; locked: number[]; bundle: string; sandbox: boolean }

export const LEVELS: [number, string][] = [[1, "The Bloch Sphere"], [2, 'The "Gate of Fate"'], [3, "The Multiverse"]];
const KEYS: [string, string][] = [
  ["← → or A D", "move"], ["↑ or W", "rotate"], ["↓ or S", "soft drop"], ["Space", "hard drop"],
  ["1 / 2", "Rx(±π/2) gate"], ["3 / 4", "Ry(±π/2) gate"], ["5 / 6", "Rz(±π/2) gate"], ["M", "measure"], ["P", "pause"],
  ["Enter", "start (on the opening screen: the first level)"], ["Esc", "back to the opening screen"],
];

/** The game's manual (tetris/manual.html, served by the web app - web/vite.config.ts). */
export const MANUAL_URL = "/qtetris-manual.html";

/** Height of the game's touch buttons under the board (tetris/config.py CONTROLS_HEIGHT; tetris/touch.py). */
export const CONTROLS_HEIGHT = 160;

/** A touch screen (phone, tablet)? Then the game shows its buttons and the frame is that much taller. */
export function isTouchScreen(): boolean {
  try {
    return !!window.matchMedia?.("(pointer: coarse)").matches;
  } catch {
    return false;
  }
}

/** The frame's address: the game's page, told whether to show the touch buttons (so frame and game agree). */
export function frameUrl(url: string, touch: boolean): string {
  return `${url.split("#")[0]}#touch=${touch ? 1 : 0}`;
}

/** Is the screen taller than wide? (follows the phone being turned) */
export function usePortrait(): boolean {
  const query = "(orientation: portrait)";
  const [on, setOn] = useState(() => { try { return !!window.matchMedia?.(query).matches; } catch { return false; } });
  useEffect(() => {
    let mq: MediaQueryList | undefined;
    try { mq = window.matchMedia?.(query); } catch { return; }
    if (!mq?.addEventListener) return;
    const on = (e: MediaQueryListEvent) => setOn(e.matches);
    mq.addEventListener("change", on);
    return () => mq!.removeEventListener("change", on);
  }, []);
  return on;
}

/** The game's message when its opening screen is up (tetris/runtime.py notify_ready). */
export const READY_MESSAGE = "qtetris:ready";

/** Is this message event the game in ``frame`` saying it is ready? (Only that frame's window counts.) */
export function isReady(e: { source: unknown; data: unknown }, frame: HTMLIFrameElement | null): boolean {
  return !!frame && e.source === frame.contentWindow && e.data === READY_MESSAGE;
}

/** The frame's sandbox: its own origin, so the game (and the Python runtime it loads) cannot reach this page. */
export function sandboxFor(s: TetrisSession): string | undefined {
  return s.sandbox ? "allow-scripts allow-pointer-lock allow-popups allow-popups-to-escape-sandbox" : undefined;
}

export function QTetrisPage() {
  const session = useQuery({ queryKey: ["tetris-session"], queryFn: () => post<TetrisSession>("/tetris/session"),
    staleTime: 60 * 60 * 1000, retry: false });
  const [asking, setAsking] = useState<string | null>(null);
  const { data: refSections } = useReferences();          // "[n]" beside the game and its levels (tetris/references)
  const frame = useRef<HTMLIFrameElement>(null);
  const s = session.data;
  const [ready, setReady] = useState(false);
  const [slow, setSlow] = useState(false);
  useEffect(() => {                                   // until the game says it is up: a "Loading" note over it
    setReady(false); setSlow(false);
    if (!s) return;
    const onMessage = (e: MessageEvent) => {
      if (isReady(e, frame.current)) { setReady(true); frame.current?.focus(); }   // the game gets the keyboard
    };
    window.addEventListener("message", onMessage);
    const t = window.setTimeout(() => setSlow(true), 90_000);
    return () => { window.removeEventListener("message", onMessage); window.clearTimeout(t); };
  }, [s?.url]);
  const width = s?.levels.includes(3) ? 900 : 640;          // Level 3 plays on two boards
  const touch = useMemo(isTouchScreen, []);
  const portrait = usePortrait();
  // The frame keeps the game's shape and fits the screen. Touch: 640 wide when the phone is upright (Levels 1-2 as
  // large as possible; Level 3 is scaled down in it), the full width when sideways - and never taller than the screen.
  const frameW = touch && portrait ? 640 : width;
  const height = 640 + (touch ? CONTROLS_HEIGHT : 0);

  return (
    <Stack>
      <PageTitle title="QTetris">
        {s && <Button variant="default" onClick={() => frame.current?.requestFullscreen?.()}>Full screen</Button>}
      </PageTitle>
      <Text c="dimmed" size="sm">
        Quantum Tetris: each falling piece is in a superposition of two pieces. Rotate its qubit on the Bloch sphere with
        the gates, then measure it - or let the quantum line measure it for you.
        <RefLinks refs={tagged(refSections, "qtetris", "qtetris")} />
      </Text>
      {session.isLoading && <Loader size="sm" />}
      {session.error instanceof ApiError && session.error.status === 503
        ? <Alert color="blue">{session.error.message}</Alert>
        : <ErrorBox error={session.error} />}
      {s && (
        <Group align="flex-start" gap="lg" wrap="wrap">
          <Card withBorder p={0} style={{ overflow: "hidden", width: frameW, position: "relative",
            maxWidth: touch ? `min(100%, calc((100dvh - 24px) * ${frameW} / ${height}))` : "100%" }}>
            <iframe ref={frame} title="QTetris" src={frameUrl(s.url, touch)} sandbox={sandboxFor(s)} allow="fullscreen; autoplay"
              style={{ border: 0, display: "block", width: "100%", aspectRatio: `${frameW} / ${height}`, background: "#000",
                touchAction: "none" }} />
            {!ready && (
              <Stack data-testid="tetris-loading" align="center" justify="center" gap="xs"
                style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.78)", color: "#fff", pointerEvents: "none" }}>
                <Loader color="white" size="sm" />
                <Text size="sm" fw={600}>Loading QTetris…</Text>
                <Text size="xs" c="gray.4" ta="center" maw={360}>
                  {slow ? "This is taking longer than usual. The game loads Python from pygbag's servers - check the internet connection, or reload the page."
                    : "The first start loads Python, numpy and pygame into your browser - about half a minute. Later starts are faster."}
                </Text>
              </Stack>
            )}
          </Card>
          <Stack gap="sm" w={380} maw="100%">
            <Button component="a" href={MANUAL_URL} target="_blank" rel="noopener" variant="light" fullWidth>
              QTetris manual - how to play
            </Button>
            {touch
              ? <Text size="xs" c="dimmed">Tap a level to start. Play with the buttons under the board: move, rotate, drop, measure,
                  pause, and the gates Rx, Ry, Rz. Level 3 plays on two boards - turn your phone sideways for it (and use Full screen).</Text>
              : <Text size="xs" c="dimmed">Choose a level with 1, 2, 3, Enter or a click. If the keys do nothing, click the game once so it has the keyboard.</Text>}
            <Card withBorder>
              <Text fw={600} size="sm" mb="xs">Levels</Text>
              <Stack gap={6}>
                {LEVELS.map(([n, title]) => {
                  const open = s.levels.includes(n);
                  return (
                    <Group key={n} justify="space-between" wrap="nowrap">
                      <Text size="sm">Level {n}: {title}<RefLinks refs={tagged(refSections, "qtetris", `qtetris-level${n}`)} /></Text>
                      {open ? <Badge color="teal" variant="light">Included</Badge>
                        : <Button size="compact-xs" variant="light" onClick={() => setAsking(`tetris.level${n}`)}>Request access</Button>}
                    </Group>
                  );
                })}
              </Stack>
              {s.locked.length > 0 && <Text size="xs" c="dimmed" mt="xs">Levels 2 and 3 are part of the Advanced plan.</Text>}
            </Card>
            <Card withBorder>
              <Text fw={600} size="sm" mb="xs">Keys</Text>
              <Table withRowBorders={false} verticalSpacing={1}>
                <Table.Tbody>
                  {KEYS.map(([k, what]) => (
                    <Table.Tr key={k}><Table.Td style={{ whiteSpace: "nowrap" }}><Text size="xs" ff="monospace">{k}</Text></Table.Td><Table.Td><Text size="xs">{what}</Text></Table.Td></Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Card>
          </Stack>
        </Group>
      )}
      {asking && <RequestAccessModal limitKey={asking} opened onClose={() => setAsking(null)} />}
    </Stack>
  );
}
